import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { errorText } from "../i18n/serverErrors";
import { resizeImageFile } from "../utils/resizeImage";
import { Modal } from "./Modal";
import { useT } from "../i18n/LanguageProvider";

/**
 * The photo flow on its own: pick or shoot → resize → optionally remove the
 * background via the server-side rembg sidecar → a before/after "keep this one
 * or the original?" review, with a fallback to the original if removal fails.
 *
 * Extracted from `PhotoInput` (G-116) so a surface that needs the flow WITHOUT
 * PhotoInput's buttons — the kiosk's delivery receiving row, where tapping the
 * row's photo opens the camera — reuses this one rather than growing a second
 * copy. `PhotoInput` is now its buttons around this hook.
 *
 * Backend bytes-on-the-wire are bounded by ITEM_IMAGE_MAX_BYTES (15 MB). We
 * also pre-resize large photos to {@link MAX_DIM} px on the client to save
 * bandwidth and rembg CPU.
 *
 * G-55: MAX_DIM matches the server's full-variant size — the old 1024 quietly
 * capped every stored item photo below the 1920 px the pipeline promises.
 * HEIC is accepted; if the browser can't decode it for the client-side resize
 * (Chrome/Android), the raw file goes up and the SERVER transcodes it.
 */

const MAX_DIM = 1920;

/**
 * The ONE list of photo types the app takes — what the server transcodes or
 * stores (`src/common/images/phone-image.ts` accepts the HEIC/HEIF sequence
 * labels iPhones put on Live Photo frames). Every photo picker's `accept`
 * and every client-side gate reads this; `DescribeBouquet` imports it.
 */
export const PHOTO_ACCEPT =
  "image/jpeg,image/png,image/webp,image/heic,image/heif,image/heic-sequence,image/heif-sequence";
const ACCEPT = PHOTO_ACCEPT;

type Phase = "idle" | "processing" | "review" | "error" | "unsupported";

/**
 * The camera input must say `image/*` (D-66), so on a desktop, where `capture`
 * is ignored, it offers every image type. Refuse what the server would refuse
 * rather than resizing, removing and uploading it first. An empty type (some
 * browsers' HEIC) is let through for the server to judge.
 */
export const isAcceptedPhoto = (file: File) =>
  !file.type || ACCEPT.split(",").includes(file.type.toLowerCase());

interface Options {
  /** Receives the file the person committed: the cut-out, or the original. */
  onCommit: (file: File) => void;
  /**
   * G-87: how this surface reaches the background remover — the staff route,
   * or the kiosk's floor-plane twin (a floor token 401s on every CRM route).
   */
  removeBackground: (file: File) => Promise<File>;
  /** Shown as the review dialog's title. */
  title: string;
  /** False commits the (resized) original straight away, with no dialog. */
  autoRemoveBackground: boolean;
}

export function usePhotoCapture({
  onCommit,
  removeBackground,
  title,
  autoRemoveBackground,
}: Options): {
  openCamera: () => void;
  openLibrary: () => void;
  /** The hidden inputs and the review dialog — render it once, anywhere. */
  ui: ReactNode;
} {
  const { t } = useT();

  // Two separate hidden inputs — one with capture, one without — so the
  // caller's choice dictates which OS picker opens.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState<string | null>(null);
  const [processedPreviewUrl, setProcessedPreviewUrl] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Each preview URL is revoked when it is replaced, cleared, or the caller
  // unmounts mid-review. (The mount-only cleanup this replaces captured the
  // first render's nulls and so never revoked anything.)
  useEffect(
    () => () => {
      if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
    },
    [originalPreviewUrl],
  );
  useEffect(
    () => () => {
      if (processedPreviewUrl) URL.revokeObjectURL(processedPreviewUrl);
    },
    [processedPreviewUrl],
  );

  // Bumped by every pick and every close, so a background removal that
  // answers after Cancel — or after the caller unmounted — is dropped
  // instead of reopening the dialog.
  const runRef = useRef(0);
  useEffect(
    () => () => {
      runRef.current += 1;
    },
    [],
  );

  function closeModal() {
    runRef.current += 1;
    setOriginalPreviewUrl(null);
    setProcessedPreviewUrl(null);
    setOriginalFile(null);
    setProcessedFile(null);
    setErrorMessage(null);
    setPhase("idle");
  }

  async function handlePicked(file: File) {
    const run = ++runRef.current;
    if (!isAcceptedPhoto(file)) {
      setErrorMessage(null);
      setPhase("unsupported");
      return;
    }
    const shouldRemove = autoRemoveBackground;
    let resized: File;
    try {
      resized = await resizeImageFile(file, MAX_DIM);
    } catch {
      resized = file;
    }
    if (run !== runRef.current) return;

    if (!shouldRemove) {
      // Skip the modal — commit the (possibly resized) original directly.
      onCommit(resized);
      return;
    }

    // Show modal with original + spinner; kick off removal.
    const origUrl = URL.createObjectURL(resized);
    setOriginalFile(resized);
    setOriginalPreviewUrl(origUrl);
    setProcessedPreviewUrl(null);
    setProcessedFile(null);
    setErrorMessage(null);
    setPhase("processing");

    try {
      const processed = await removeBackground(resized);
      if (run !== runRef.current) return;
      const procUrl = URL.createObjectURL(processed);
      setProcessedFile(processed);
      setProcessedPreviewUrl(procUrl);
      setPhase("review");
    } catch (err: unknown) {
      if (run !== runRef.current) return;
      const msg =
        errorText(err, "photoInput.couldNotRemoveBackground");
      setErrorMessage(msg);
      setPhase("error");
    }
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so the same filename can be re-selected later.
    e.target.value = "";
    if (!file) return;
    void handlePicked(file);
  }

  function commitOriginal() {
    if (originalFile) onCommit(originalFile);
    closeModal();
  }

  function commitProcessed() {
    if (processedFile) onCommit(processedFile);
    closeModal();
  }

  const openCamera = () => cameraInputRef.current?.click();
  const openLibrary = () => fileInputRef.current?.click();

  function retake() {
    closeModal();
    openCamera();
  }

  const ui = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        // D-66: exactly "image/*", never the ACCEPT list. The tablet shell's
        // WebView (Capacitor) opens the camera only when the accept list
        // contains the literal "image/*" — anything else goes to the gallery.
        // A camera shot is a JPEG, so nothing the list allows is lost.
        accept="image/*"
        // capture=environment opens the rear camera on mobile and is harmlessly
        // ignored on desktop (falls back to the regular picker).
        capture="environment"
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />

      {/* Portalled to <body>: a fixed overlay rendered inside a kiosk floor
          panel is trapped by the panel's backdrop-filter (G-73), and one
          inside a done delivery row would inherit its 0.72 opacity. The
          backdrop's z-index (300) already sits above the kiosk's own
          overlays (50), so the item card's stacking is unchanged. */}
      {phase !== "idle"
        ? createPortal(
            <Modal title={title} onClose={closeModal}>
              {phase === "processing" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  <p>{t("photoInput.removingBackground")}</p>
                  {originalPreviewUrl ? (
                    <img
                      src={originalPreviewUrl}
                      alt=""
                      style={{
                        maxWidth: "100%",
                        maxHeight: "40vh",
                        objectFit: "contain",
                        border: "1px solid var(--color-border-soft)",
                      }}
                    />
                  ) : null}
                  <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                    {t("photoInput.takesAFewSeconds")}
                  </p>
                </div>
              ) : null}

              {phase === "review" && originalPreviewUrl && processedPreviewUrl ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                    {t("photoInput.reviewQuestion")}
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "var(--space-2)",
                    }}
                  >
                    <figure style={{ margin: 0 }}>
                      <figcaption className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
                        {t("photoInput.original")}
                      </figcaption>
                      <img
                        src={originalPreviewUrl}
                        alt=""
                        style={{
                          width: "100%",
                          maxHeight: "40vh",
                          objectFit: "contain",
                          border: "1px solid var(--color-border-soft)",
                        }}
                      />
                    </figure>
                    <figure style={{ margin: 0 }}>
                      <figcaption className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
                        {t("photoInput.backgroundRemoved")}
                      </figcaption>
                      <img
                        src={processedPreviewUrl}
                        alt=""
                        style={{
                          width: "100%",
                          maxHeight: "40vh",
                          objectFit: "contain",
                          // Checkered pattern reveals transparency.
                          backgroundImage:
                            "linear-gradient(45deg, #ddd 25%, transparent 25%), linear-gradient(-45deg, #ddd 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ddd 75%), linear-gradient(-45deg, transparent 75%, #ddd 75%)",
                          backgroundSize: "16px 16px",
                          backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
                          border: "1px solid var(--color-border-soft)",
                        }}
                      />
                    </figure>
                  </div>
                  <div className="button-row" style={{ justifyContent: "flex-end" }}>
                    <button type="button" className="button plain" onClick={retake}>
                      {t("photoInput.retake")}
                    </button>
                    <button type="button" className="button plain" onClick={commitOriginal}>
                      {t("photoInput.useOriginal")}
                    </button>
                    <button type="button" className="button" onClick={commitProcessed}>
                      {t("photoInput.useThis")}
                    </button>
                  </div>
                </div>
              ) : null}

              {phase === "unsupported" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  <div className="alert error">{t("photoInput.unsupportedType")}</div>
                  <div className="button-row" style={{ justifyContent: "flex-end" }}>
                    <button type="button" className="button" onClick={closeModal}>
                      {t("common.close")}
                    </button>
                  </div>
                </div>
              ) : null}

              {phase === "error" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  <div className="alert error">
                    {t("photoInput.couldNotRemoveBackground")} {errorMessage}
                  </div>
                  {originalPreviewUrl ? (
                    <img
                      src={originalPreviewUrl}
                      alt=""
                      style={{
                        maxWidth: "100%",
                        maxHeight: "40vh",
                        objectFit: "contain",
                        border: "1px solid var(--color-border-soft)",
                      }}
                    />
                  ) : null}
                  <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                    {t("photoInput.errorHint")}
                  </p>
                  <div className="button-row" style={{ justifyContent: "flex-end" }}>
                    <button type="button" className="button plain" onClick={closeModal}>
                      {t("common.cancel")}
                    </button>
                    <button type="button" className="button" onClick={commitOriginal}>
                      {t("photoInput.useOriginal")}
                    </button>
                  </div>
                </div>
              ) : null}
            </Modal>,
            document.body,
          )
        : null}
    </>
  );

  return { openCamera, openLibrary, ui };
}
